// src/features/links/api.ts
// IR-1047 — TanStack Query hooks for the Join Links workspace.
// Uses the project's shared `api` helper (see @/lib/api), which already
// handles `credentials: include`, JWT refresh on 401 and JSON parsing.

import { api, apiBlob } from "@/lib/api";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import type {
  LinkOnboardingRow,
  LinksResponse,
  OperationalStatus,
  Platform,
  TargetPreview,
} from "./types";

export interface LinksFilters {
  date_from?: string;
  date_to?: string;
  source_group_id?: number[];
  avatar_id?: number[];
  platform?: Platform[];
  operational_status?: string[];
  approval_required?: boolean;
  assigned_avatar_id?: number[];
  /** IR-1047 (Sean): finder avatar ids — the earliest-collected source_avatar per link, OR-combined. */
  avatar_finder_id?: number[];
  /** IR-1047 (Rotem 45432): CSV of classification names, OR-combined server-side. */
  classification?: string;
  /** IR-1047 (Rotem 45432): risk bands (low|medium|high), multi-select OR. */
  risk_band?: string[];
  /** IR-1047: target kinds (user|group|channel|bot|unknown), multi-select OR. */
  target_kind?: string[];
  q?: string;
  sort?: string;
  dir?: "asc" | "desc";
  cursor?: number;
  limit?: number;
}

/** Serialise LinksFilters as a URLSearchParams query string (no leading ?).
 *  Keys are emitted in sorted order so that two structurally-equal filter
 *  objects built by different code paths produce the same string — which in
 *  turn means TanStack Query treats them as the same cache entry. */
export function linksFiltersToQuery(filters: LinksFilters): string {
  const qs = new URLSearchParams();
  const keys = Object.keys(filters).sort();
  for (const k of keys) {
    const v = (filters as Record<string, unknown>)[k];
    if (v === undefined || v === null) continue;
    if (Array.isArray(v)) {
      for (const x of v) qs.append(k, String(x));
    } else {
      qs.set(k, String(v));
    }
  }
  return qs.toString();
}

/** IR-1386 — rows per page for the numbered pagination. */
export const LINKS_PAGE_SIZE = 50;

/**
 * IR-1386 (Tomer) — page-at-a-time reader for the Join Links table, replacing
 * the "Load more" infinite list with numbered pages.
 *
 * No backend change was needed: `cursor` is a plain row offset and the response
 * already carries the full filtered `total`, so page N is offset (N-1)*size and
 * the page count is ceil(total/size). `placeholderData` keeps the previous
 * page's rows on screen while the next one loads, so paging doesn't flash the
 * table through an empty state.
 */
export function useLinksPaged(filters: LinksFilters, page: number, pageSize = LINKS_PAGE_SIZE) {
  const baseQs = linksFiltersToQuery({
    ...filters,
    limit: pageSize,
    cursor: (page - 1) * pageSize,
  });
  const query = useQuery<LinksResponse>({
    queryKey: ["links-paged", baseQs],
    queryFn: () => api<LinksResponse>(`/api/ops/v1/fleet/links?${baseQs}`),
    placeholderData: (prev) => prev,
  });
  const total = query.data?.total ?? 0;
  return {
    items: query.data?.items ?? [],
    total,
    pageCount: Math.max(1, Math.ceil(total / pageSize)),
    // IR-1233 r3 — unfiltered active-queue count for the "X / Y links" title.
    grandTotal: query.data?.grand_total ?? null,
    classificationOptions: query.data?.classification_options ?? [],
    // IR-1386 — what the default view hid; null until the backend ships it.
    noiseHidden: query.data?.noise_hidden ?? null,
    // IR-1386 r3 — per-type counts for the "By type" pie; null until the
    // backend ships the facet, which is what keeps this FE deploy inert.
    targetKindCounts: query.data?.target_kind_counts ?? null,
    // IR-1386 r4 — {tg|wa|signal: n} over the same filtered set; null until the
    // backend facet ships, which keeps this FE deploy inert (the r2 ordering).
    platformCounts: query.data?.platform_counts ?? null,
    // isLoading is false while showing placeholder data from the previous page,
    // so the table stays mounted; isFetching drives the "paging…" affordance.
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isSuccess: query.isSuccess,
    error: query.error,
    refetch: query.refetch,
  };
}

/** List link-onboarding rows with optional filters. */
export function useLinks(filters: LinksFilters) {
  const qs = linksFiltersToQuery(filters);
  return useQuery<LinksResponse>({
    queryKey: ["links", qs],
    queryFn: () => api<LinksResponse>(`/api/ops/v1/fleet/links${qs ? `?${qs}` : ""}`),
  });
}

/**
 * Cursor-paginated variant of `useLinks` for the "Load more" UX. Pages are
 * concatenated into a flat `items` array. `hasNextPage` is driven by the
 * server's `next_cursor` (null → no more pages).
 */
export function useLinksInfinite(filters: LinksFilters) {
  const baseQs = linksFiltersToQuery(filters);
  const query = useInfiniteQuery<LinksResponse>({
    queryKey: ["links-infinite", baseQs],
    initialPageParam: undefined as number | undefined,
    queryFn: ({ pageParam }) => {
      const qs = new URLSearchParams(baseQs);
      if (pageParam !== undefined) qs.set("cursor", String(pageParam));
      const suffix = qs.toString();
      return api<LinksResponse>(`/api/ops/v1/fleet/links${suffix ? `?${suffix}` : ""}`);
    },
    getNextPageParam: (lastPage) => lastPage.next_cursor ?? undefined,
  });
  const items = useMemo<LinkOnboardingRow[]>(
    () => (query.data?.pages ?? []).flatMap((p) => p.items),
    [query.data],
  );
  const total = query.data?.pages[0]?.total ?? 0;
  // IR-1233 r3 — unfiltered active-queue count for the "X / Y links" title.
  // Null when the backend does not send it yet (FE ships independently).
  const grandTotal = query.data?.pages[0]?.grand_total ?? null;
  // Classification filter options (data-driven facet) ride on every page; read
  // from the first page so the dropdown is populated as soon as data lands.
  const classificationOptions = query.data?.pages[0]?.classification_options ?? [];
  return {
    items,
    total,
    grandTotal,
    classificationOptions,
    hasNextPage: query.hasNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
    fetchNextPage: query.fetchNextPage,
    isLoading: query.isLoading,
    isSuccess: query.isSuccess,
    error: query.error,
    refetch: query.refetch,
  };
}

// IR-1047 (Rotem 2026-06-15): option for the "Group" (originating source group)
// filter — the distinct source groups that produced links, {id, name}.
export interface SourceGroupOption {
  id: number;
  name: string;
}

/**
 * Options for the "Group" filter (originating source group). Fetched once and
 * cached client-side (the group vocabulary is larger than the classification
 * facet but stable), mirroring how `useFleetAvatars` backs the Avatar (finder)
 * filter rather than riding a per-page response facet.
 */
export function useLinkSourceGroups() {
  return useQuery({
    queryKey: ["links", "source-groups"],
    queryFn: async () => {
      const resp = await api<{ items: SourceGroupOption[] }>(
        "/api/ops/v1/fleet/links/source-groups",
      );
      return resp.items;
    },
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Download the current Join Links result set as CSV (IR-1047, Rotem 2026-06-15).
 * Re-uses the active `filters` (incl. sort/dir) so the file matches the table;
 * the backend streams the FULL filtered set, not just the loaded pages. Fetched
 * as a Blob through the auth-aware helper, then handed to the browser via a
 * transient object URL + anchor click.
 */
export async function downloadLinksCsv(filters: LinksFilters): Promise<void> {
  const qs = linksFiltersToQuery(filters);
  const blob = await apiBlob(`/api/ops/v1/fleet/links/export${qs ? `?${qs}` : ""}`);
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "join_links_export.csv";
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// IR-1047 Phase 2 — operational_status values an analyst may set (OPS-03).
// Mirrors the backend allowlist _SETTABLE_OPERATIONAL_STATUSES; 'failed' is
// auto-only (OPS-02) and 'obsolete' is dismiss-only (Phase 3) — both are
// intentionally NOT in this list (obsolete is reached via the Dismiss action).
export const SETTABLE_OPERATIONAL_STATUSES: OperationalStatus[] = [
  "new",
  "in_review",
  "waiting4approval",
  "in_coverage",
  // IR-1047 (Rotem 45432): admin declined our join request. Mirrors the BE allowlist.
  "rejected",
  // IR-1047 (Tomer/Rotem 2026-06-15): invite link expired/changed. Mirrors the BE allowlist.
  "broken",
];

export interface LinkUpdatePayload {
  operational_status?: OperationalStatus;
  approval_required?: boolean;
}

/**
 * Set operational_status and/or approval_required on a single link row
 * (PATCH /api/ops/v1/fleet/links/<id>, IR-1047 Phase 2). On success the
 * links list queries are invalidated so the table re-reads the canonical row.
 */
export function useUpdateLink() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: number; patch: LinkUpdatePayload }) =>
      api(`/api/ops/v1/fleet/links/${id}`, {
        method: "PATCH",
        body: JSON.stringify(patch),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["links-infinite"] });
      qc.invalidateQueries({ queryKey: ["links"] });
    },
  });
}

export interface ManualAddPayload {
  link: string;
  approval_required?: boolean;
}

/**
 * Manually add a link to the onboarding queue (POST /api/ops/v1/fleet/links,
 * IR-1047 Phase 3). The backend classifies + normalizes the raw link (IM-only)
 * and dedups against any existing row. Invalidates the list on success.
 */
export function useCreateLink() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: ManualAddPayload) =>
      api("/api/ops/v1/fleet/links", {
        method: "POST",
        body: JSON.stringify(payload),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["links-infinite"] });
      qc.invalidateQueries({ queryKey: ["links"] });
    },
  });
}

/**
 * Response of POST /api/ops/v1/fleet/links/<id>/resolve-preview (IR-1047): the
 * freshly-resolved `target_preview` block plus a `resolve_status` summarizing
 * the resolution outcome ("ok" | "unsupported" | "failed").
 */
export interface ResolvePreviewResponse {
  target_preview: TargetPreview;
  resolve_status: string;
}

/**
 * Resolve (fetch on demand) the target-group preview for a single link
 * (POST /api/ops/v1/fleet/links/<id>/resolve-preview, IR-1047). On success the
 * links list queries are invalidated so the table re-reads the row with its now
 * populated `target_preview` block. Mirrors `useUpdateLink` / `useDismissLink`.
 */
export function useResolvePreview() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      api<ResolvePreviewResponse>(`/api/ops/v1/fleet/links/${id}/resolve-preview`, {
        method: "POST",
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["links-infinite"] });
      qc.invalidateQueries({ queryKey: ["links"] });
    },
  });
}

/**
 * Dismiss a link (POST /api/ops/v1/fleet/links/<id>/dismiss, IR-1047 Phase 3):
 * terminal -> operational_status='obsolete' + leaves the active queue. The row
 * disappears from the list on the post-success refetch.
 */
export function useDismissLink() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api(`/api/ops/v1/fleet/links/${id}/dismiss`, { method: "POST" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["links-infinite"] });
      qc.invalidateQueries({ queryKey: ["links"] });
    },
  });
}
